import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { authApi, projectsApi } from '../../api/services';
import type { Project } from '../../types/models';
import { transcriptsCopy } from '../../copy/transcripts';

// ---------------------------------------------------------------------------
// AssignDialog — pick a target executive (same tenant), a project, or both,
// then call back with the selected IDs. The dialog does not hit the assign
// endpoint itself; the parent decides when to submit so it can wire its own
// success / error handling.
// ---------------------------------------------------------------------------

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetExecutiveUserId?: string, projectId?: string) => Promise<void> | void;
  extractionTitle?: string;
}

export function AssignDialog({
  open,
  onClose,
  onConfirm,
  extractionTitle,
}: AssignDialogProps) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      authApi.getUsers().catch(() => [] as UserRow[]),
      projectsApi.getAll({ includeArchived: false }).catch(() => [] as Project[]),
    ])
      .then(([u, p]) => {
        if (cancelled) return;
        setUsers((u as UserRow[]).filter((x) => x.isActive));
        setProjects(p as Project[]);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Unable to load executives and projects.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset selection when the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setSelectedProject(null);
      setSubmitError(null);
    }
  }, [open]);

  const canSubmit = useMemo(
    () => Boolean(selectedUser || selectedProject),
    [selectedUser, selectedProject],
  );

  const handleConfirm = async () => {
    if (!canSubmit) {
      setSubmitError(transcriptsCopy.assignValidationChoose);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onConfirm(selectedUser?.id, selectedProject?.id);
      onClose();
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error ?? e?.message ?? 'Unable to assign.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{transcriptsCopy.assignDialogTitle}</DialogTitle>
      <DialogContent>
        {extractionTitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {extractionTitle}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mb: 3 }}>
          {transcriptsCopy.assignDialogBody}
        </Typography>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Autocomplete
              value={selectedUser}
              onChange={(_, v) => setSelectedUser(v)}
              options={users}
              getOptionLabel={(u) => `${u.displayName} (${u.email})`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={transcriptsCopy.assignToExecutiveLabel}
                  placeholder={transcriptsCopy.assignSelectExecutive}
                  size="small"
                />
              )}
              isOptionEqualToValue={(a, b) => a.id === b.id}
            />
            <Autocomplete
              value={selectedProject}
              onChange={(_, v) => setSelectedProject(v)}
              options={projects}
              getOptionLabel={(p) => p.title}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={transcriptsCopy.assignToProjectLabel}
                  placeholder={transcriptsCopy.assignSelectProject}
                  size="small"
                />
              )}
              isOptionEqualToValue={(a, b) => a.id === b.id}
            />
          </Stack>
        )}

        {loadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {loadError}
          </Alert>
        )}
        {submitError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {transcriptsCopy.assignCancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Assigning…' : transcriptsCopy.assignConfirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignDialog;
